import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Atom } from 'react-loading-indicators';

const HUBSPOT_PER_PAGE = 100;

const HUBSPOT_TABLE_COLUMNS = [
  { key: 'createdate', label: 'Create date' },
  { key: 'firstname', label: 'First name' },
  { key: 'lastname', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'company', label: 'Company' },
  { key: 'country', label: 'Country' },
  { key: 'website', label: 'Website' },
  { key: 'business_model', label: 'Business model' },
  { key: 'budget', label: 'Budget' },
  { key: 'hs_lead_status', label: 'Lead status' },
  { key: 'hs_object_source', label: 'Object source' },
  { key: 'lastmodifieddate', label: 'Last modified' },
];

const HUBSPOT_TABLE_COLUMN_CELL_STYLE = {
  business_model: { width: '180px', maxWidth: '180px' },
  budget: { width: '160px', maxWidth: '160px' },
  website: { width: '160px', maxWidth: '160px' },
};

function formatCellValue(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseContentDispositionFilename(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== 'string') {
    return 'hubspot_buyer_contacts.csv';
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/['"]/g, '').trim());
    } catch {
      return 'hubspot_buyer_contacts.csv';
    }
  }
  const quoted = contentDisposition.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1].trim();
  const unquoted = contentDisposition.match(/filename=([^;]+)/i);
  if (unquoted) return unquoted[1].replace(/['"]/g, '').trim();
  return 'hubspot_buyer_contacts.csv';
}

function HubspotBuyerSearch() {
  const [businessModels, setBusinessModels] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [businessModelOptions, setBusinessModelOptions] = useState([]);
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);

  const styles = {
    pageRoot: {
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box',
      padding: '10px clamp(16px, 4vw, 40px) 32px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    formContainer: {
      width: '100%',
      maxWidth: '560px',
      margin: '0 auto',
      transition: 'all 0.5s ease',
      padding: '20px',
      boxSizing: 'border-box',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerText: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      marginTop: '10px',
      marginBottom: '20px',
      borderBottom: '1px solid black',
    },
    label: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      marginTop: '10px',
      marginBottom: '2px',
      fontWeight: 'bold',
    },
    button: {
      margin: '10px 0',
      padding: '10px',
      backgroundColor: '#6487dc',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '4px',
    },
    resultsBar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      marginBottom: '10px',
    },
    exportButton: {
      padding: '8px 14px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      borderRadius: '4px',
      border: '1.5px solid #6487dc',
      backgroundColor: '#fff',
      color: '#6487dc',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    exportButtonDisabled: {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    checkboxGroupTwoCol: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px',
      margin: '5px 0',
      padding: '8px',
      border: '1px solid #cccccc',
      borderRadius: '4px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      maxHeight: '220px',
      overflowY: 'auto',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
    },
    checkboxInput: {
      margin: '2px 0 0 0',
      cursor: 'pointer',
      flexShrink: 0,
    },
    resultsSection: {
      width: '100%',
      marginTop: '24px',
      boxSizing: 'border-box',
      flex: '1 1 auto',
    },
    resultsMeta: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      marginBottom: '8px',
      color: '#333',
    },
    tableWrap: {
      width: '100%',
      overflowX: 'auto',
      border: '1px solid #cccccc',
      borderRadius: '4px',
      backgroundColor: '#fafafa',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      tableLayout: 'auto',
      borderCollapse: 'collapse',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
    },
    th: {
      textAlign: 'left',
      padding: '8px 10px',
      borderBottom: '2px solid #6487dc',
      backgroundColor: '#eef2fb',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
    },
    td: {
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'top',
      wordBreak: 'break-word',
    },
    pagi: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '20px',
      flexWrap: 'wrap',
    },
    pagiBtn: {
      padding: '8px 14px',
      border: '1.5px solid #ccc',
      borderRadius: '4px',
      background: '#fff',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#555',
      cursor: 'pointer',
    },
    pagiBtnDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    pagiInfo: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      color: '#555',
      padding: '0 8px',
    },
    trEven: {
      backgroundColor: '#fff',
    },
    trOdd: {
      backgroundColor: '#f7f9fc',
    },
    optionsHint: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      color: '#666',
      margin: '5px 0',
    },
  };

  useEffect(() => {
    setOptionsLoading(true);
    axios
      .get('/api/hubspot_buyer_search')
      .then((res) => {
        const bm = Array.isArray(res.data?.businessModels) ? res.data.businessModels : [];
        const bud = Array.isArray(res.data?.budgets) ? res.data.budgets : [];
        setBusinessModelOptions(bm);
        setBudgetOptions(bud);
      })
      .catch((err) => {
        console.error('Error loading HubSpot filter options:', err);
        setBusinessModelOptions([]);
        setBudgetOptions([]);
        alert('Could not load filter options. Check the HubSpot table and API.');
      })
      .finally(() => setOptionsLoading(false));
  }, []);

  const toggleMulti = (value, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const buildPayload = () => ({
    businessModels,
    budgets,
  });

  const handleExportCsv = () => {
    setExportLoading(true);
    axios
      .post('/api/export_hubspot_buyer_data', buildPayload(), {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        const cd = response.headers['content-disposition'];
        const filename = parseContentDispositionFilename(cd);
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error exporting HubSpot buyer CSV:', error);
        alert('Could not export CSV. Please try again.');
      })
      .finally(() => {
        setExportLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResults([]);
    setPage(1);

    axios
      .post('/api/hubspot_buyer_search', buildPayload(), {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        setIsLoading(false);
        setHasSearched(true);
        if (response.status === 200 && Array.isArray(response.data)) {
          setResults(response.data);
          setPage(1);
        } else {
          setResults([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setResults([]);
        setHasSearched(false);
        console.error('Error submitting HubSpot buyer search:', error);
        alert('Error submitting search. Please try again.');
      });
  };

  const totalPages = Math.max(1, Math.ceil(results.length / HUBSPOT_PER_PAGE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const slice = useMemo(() => {
    const start = (pageSafe - 1) * HUBSPOT_PER_PAGE;
    return results.slice(start, start + HUBSPOT_PER_PAGE);
  }, [results, pageSafe]);

  const rangeStart = results.length === 0 ? 0 : (pageSafe - 1) * HUBSPOT_PER_PAGE + 1;
  const rangeEnd = Math.min(results.length, pageSafe * HUBSPOT_PER_PAGE);

  return (
    <div style={styles.pageRoot}>
      <div style={styles.formContainer}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.headerText}>HubSpot Buyer Search</h3>

          <label style={styles.label}>Business model</label>
          {optionsLoading ? (
            <p style={styles.optionsHint}>Loading options…</p>
          ) : businessModelOptions.length === 0 ? (
            <p style={styles.optionsHint}>No business model values found in the table.</p>
          ) : (
            <div style={styles.checkboxGroupTwoCol}>
              {businessModelOptions.map((opt) => (
                <label key={opt} style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={businessModels.includes(opt)}
                    onChange={() => toggleMulti(opt, setBusinessModels)}
                    style={styles.checkboxInput}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          )}

          <label style={styles.label}>Budget</label>
          {optionsLoading ? (
            <p style={styles.optionsHint}>Loading options…</p>
          ) : budgetOptions.length === 0 ? (
            <p style={styles.optionsHint}>No budget values found in the table.</p>
          ) : (
            <div style={styles.checkboxGroupTwoCol}>
              {budgetOptions.map((opt) => (
                <label key={opt} style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={budgets.includes(opt)}
                    onChange={() => toggleMulti(opt, setBudgets)}
                    style={styles.checkboxInput}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          )}

          {isLoading && (
            <div align="center" style={{ marginTop: '20px' }}>
              <Atom color="#32cd32" size="medium" text="Searching..." />
            </div>
          )}

          <button type="submit" disabled={isLoading || optionsLoading} style={styles.button}>
            Search
          </button>
        </form>
      </div>

      <div style={styles.resultsSection}>
        {!isLoading && hasSearched && results.length === 0 && (
          <div style={styles.resultsBar}>
            <p style={{ ...styles.resultsMeta, margin: 0 }}>No contacts match your filters.</p>
            <button
              type="button"
              disabled={exportLoading}
              onClick={handleExportCsv}
              style={{
                ...styles.exportButton,
                ...(exportLoading ? styles.exportButtonDisabled : {}),
              }}
            >
              {exportLoading ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        )}
        {!isLoading && results.length > 0 && (
          <>
            <div style={styles.resultsBar}>
              <p style={{ ...styles.resultsMeta, margin: 0 }}>
                {results.length} contact{results.length === 1 ? '' : 's'} found · Showing {rangeStart}–
                {rangeEnd}
              </p>
              <button
                type="button"
                disabled={exportLoading}
                onClick={handleExportCsv}
                style={{
                  ...styles.exportButton,
                  ...(exportLoading ? styles.exportButtonDisabled : {}),
                }}
              >
                {exportLoading ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {HUBSPOT_TABLE_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        style={{ ...styles.th, ...HUBSPOT_TABLE_COLUMN_CELL_STYLE[col.key] }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slice.map((row, rowIndex) => (
                    <tr
                      key={(pageSafe - 1) * HUBSPOT_PER_PAGE + rowIndex}
                      style={rowIndex % 2 === 0 ? styles.trEven : styles.trOdd}
                    >
                      {HUBSPOT_TABLE_COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          style={{ ...styles.td, ...HUBSPOT_TABLE_COLUMN_CELL_STYLE[col.key] }}
                        >
                          {formatCellValue(row[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {results.length > HUBSPOT_PER_PAGE && (
              <div style={styles.pagi}>
                <button
                  type="button"
                  style={{
                    ...styles.pagiBtn,
                    ...(pageSafe <= 1 || isLoading ? styles.pagiBtnDisabled : {}),
                  }}
                  disabled={pageSafe <= 1 || isLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={styles.pagiInfo}>
                  Page {pageSafe} of {totalPages}
                </span>
                <button
                  type="button"
                  style={{
                    ...styles.pagiBtn,
                    ...(pageSafe >= totalPages || isLoading ? styles.pagiBtnDisabled : {}),
                  }}
                  disabled={pageSafe >= totalPages || isLoading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HubspotBuyerSearch;
